import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";
import { X, ScanLine, Keyboard } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TeamDriverData } from "@/store/portal-store";

interface AddDriverModalProps {
  onAdd: (driver: TeamDriverData) => void;
  onClose: () => void;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

const CMV_OPTIONS = [
  { value: "lt1", label: "<1 yr" },
  { value: "1to3", label: "1-3" },
  { value: "3to5", label: "3-5" },
  { value: "5plus", label: "5+" },
];

export function AddDriverModal({ onAdd, onClose }: AddDriverModalProps) {
  const [tab, setTab] = useState<"scan" | "manual">("scan");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [issuingState, setIssuingState] = useState("");
  const [licenseClass, setLicenseClass] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [cmvExperience, setCmvExperience] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [saving, setSaving] = useState(false);

  const manualComplete =
    firstName.length >= 1 &&
    lastName.length >= 1 &&
    dob.length >= 8 &&
    licenseNumber.length >= 1 &&
    issuingState &&
    licenseClass &&
    expirationDate.length >= 8 &&
    cmvExperience;

  const canSubmit = tab === "scan" ? scanned && cmvExperience : manualComplete;

  const handleScan = () => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      setScanned(true);
      setFirstName("James");
      setLastName("Wilson");
      setDob("05/12/1985");
      setLicenseNumber("D123-4567-8901");
      setIssuingState("TX");
      setLicenseClass("A");
      setExpirationDate("09/15/2027");
    }, 3000);
  };

  const handleAdd = () => {
    setSaving(true);
    setTimeout(() => {
      onAdd({
        firstName,
        lastName,
        dob,
        licenseNumber,
        issuingState,
        licenseClass,
        expirationDate,
        cmvExperience,
      });
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h2 className="text-lg font-bold">Add a Team Driver</h2>
        <button type="button" onClick={onClose}>
          <X className="w-6 h-6 text-gray-400" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          type="button"
          onClick={() => setTab("scan")}
          className={cn(
            "flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors flex items-center justify-center gap-2",
            tab === "scan"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500"
          )}
        >
          <ScanLine className="w-4 h-4" /> Scan License
        </button>
        <button
          type="button"
          onClick={() => setTab("manual")}
          className={cn(
            "flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors flex items-center justify-center gap-2",
            tab === "manual"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500"
          )}
        >
          <Keyboard className="w-4 h-4" /> Enter Manually
        </button>
      </div>

      <div className="px-6 py-6 space-y-6">
        {tab === "scan" && (
          <>
            {!scanned ? (
              <div
                className="bg-gray-900 rounded-2xl h-64 flex flex-col items-center justify-center cursor-pointer"
                onClick={handleScan}
              >
                {scanning ? (
                  <>
                    <Spinner size="md" className="border-white border-t-blue-400 mb-3" />
                    <p className="text-white/60 text-sm">Scanning license...</p>
                  </>
                ) : (
                  <>
                    <ScanLine className="w-12 h-12 text-white/60 mb-3" />
                    <p className="text-white/60 text-sm">Tap to scan driver's license</p>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1">
                <p className="font-medium text-green-700">License scanned successfully</p>
                <p className="text-sm text-gray-600">
                  {firstName} {lastName} — {issuingState} {licenseNumber}
                </p>
              </div>
            )}
          </>
        )}

        {tab === "manual" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-1">
                <Label>Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-12"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="space-y-1">
              <Label>Driver's License Number</Label>
              <Input
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Issuing State</Label>
                <Select value={issuingState} onValueChange={setIssuingState}>
                  <SelectTrigger className="h-12 w-full">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((st) => (
                      <SelectItem key={st} value={st}>
                        {st}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>License Class</Label>
                <Select value={licenseClass} onValueChange={setLicenseClass}>
                  <SelectTrigger className="h-12 w-full">
                    <SelectValue placeholder="Class" />
                  </SelectTrigger>
                  <SelectContent>
                    {["A", "B", "C"].map((cl) => (
                      <SelectItem key={cl} value={cl}>
                        Class {cl}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>License Expiration Date</Label>
              <Input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="h-12"
              />
            </div>
          </div>
        )}

        {/* CMV Experience - both tabs */}
        {(tab === "manual" || (tab === "scan" && scanned)) && (
          <div className="space-y-2">
            <Label className="text-base">CMV driving experience?</Label>
            <div className="flex gap-2">
              {CMV_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCmvExperience(opt.value)}
                  className={cn(
                    "flex-1 px-3 py-2.5 rounded-full border text-sm font-medium transition-all",
                    cmvExperience === opt.value
                      ? "border-blue-500 bg-blue-500 text-white"
                      : "border-gray-200 bg-white text-gray-700"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MVR Disclosure */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <p className="text-xs text-blue-700">
            Note: A motor vehicle report (MVR) will be pulled for this driver
            during the binding process. This may affect your premium.
          </p>
        </div>

        <Button
          className="w-full h-12 text-base"
          disabled={!canSubmit || saving}
          onClick={handleAdd}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" className="border-white border-t-transparent" />
              Adding driver...
            </span>
          ) : (
            "Add Driver"
          )}
        </Button>
      </div>
    </div>
  );
}
